import * as React from "react";

import * as moment from "moment";
import {
  DatePicker,
  DatePickerInput,
  ModalWrapper,
  RadioButton,
  RadioButtonGroup,
  TimePicker,
  TimePickerSelect,
  Loading,
  SelectItem,
} from "carbon-components-react";

import APIClient from "web_modules/APIClient";
import { AppShape } from "@eave/shared/types/api";

interface CloneModalButtonProps {
  app: AppShape;
}

const CloneModalButton: React.FC<CloneModalButtonProps> = (props: CloneModalButtonProps) => {
  const keywordValueOther = "other";
  const keywordValueConditionalApproval = "conditional_approval";
  const keywordValueNow = "now";

  const [cloneKeyword, setCloneKeyword] = React.useState(null);
  const [cloneDate, setCloneDate] = React.useState("");
  const [cloneTime, setCloneTime] = React.useState("");
  const [cloneMeridiem, setCloneMeridiem] = React.useState(null);
  const [cloneID, setCloneID] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState(null);

  const timePattern = /^(1[012]|[1-9]):[0-5][0-9]$/;

  const timeIsValid = () => {
    return timePattern.test(cloneTime);
  };
  const dateIsValid = () => {
    return moment(cloneDate).isValid();
  };
  const validForDateTimeInput = () => {
    return cloneKeyword === keywordValueOther && timeIsValid() && dateIsValid();
  };
  const validForKeywordInput = () => {
    return cloneKeyword === keywordValueConditionalApproval || cloneKeyword === keywordValueNow;
  };

  function clone(): Promise<any> {
    const { app } = props;
    const { id } = app;

    setSubmitting(true);

    if (validForDateTimeInput()) {
      // convert cloneDate and cloneTime and cloneMeridiem into a UTC datetime
      const timeComponents = cloneTime.split(":");
      let hours = Number(timeComponents[0]);
      const minutes = Number(timeComponents[1]);
      if (cloneMeridiem === "PM") {
        hours += 12;
      }

      const t = moment(cloneDate).hours(hours).minutes(minutes); // in local time
      return APIClient.post(`/uw/loan_applications/${id}/clone`, {
        copy_as_of_datetime: t.toJSON(), // toJSON converts to UTC
      })
        .then(({ newID }) => {
          setCloneID(newID);
          setSubmitting(false);
        })
        .catch(({ meta }) => {
          const { error_detail } = meta;
          setError(error_detail);
          setSubmitting(false);
        });
    }
    if (validForKeywordInput()) {
      return APIClient.post(`/uw/loan_applications/${id}/clone`, {
        copy_as_of_keyword: cloneKeyword,
      })
        .then(({ newID }) => {
          setCloneID(newID);
          setSubmitting(false);
        })
        .catch(({ meta }) => {
          const { error_detail } = meta;
          setError(error_detail);
          setSubmitting(false);
        });
    }
    return Promise.resolve();
  }

  return (
    <div style={{ float: "right" }}>
      <ModalWrapper
        modalHeading="Clone Loan"
        buttonTriggerText="Clone"
        handleSubmit={clone}
        primaryButtonDisabled={
          (!validForKeywordInput() && !validForDateTimeInput()) ||
          cloneID !== null ||
          error !== null
        }
      >
        {submitting && <Loading description="Active loading indicator" withOverlay={true} />}
        {cloneID && (
          <>
            <h1>Success!</h1>
            <p>New App ID: {`${cloneID}`}</p>
          </>
        )}
        {error && (
          <>
            <h1>Error!</h1>
            <p>{`${error}`}</p>
          </>
        )}
        {cloneID === null && error === null && (
          <>
            <p>Clone up until:</p>
            <RadioButtonGroup
              name="time_period_option"
              defaultSelected="default-selected"
              onChange={value => {
                setCloneKeyword(value);
              }}
            >
              <RadioButton value={keywordValueNow} id="radio-1" labelText="Now" />
              <RadioButton
                value={keywordValueConditionalApproval}
                labelText="Conditional Approval"
                id="radio-2"
              />
              <RadioButton value={keywordValueOther} labelText="Other" id="radio-3" />
            </RadioButtonGroup>
            <br />
            {cloneKeyword === keywordValueOther && (
              <>
                <DatePicker
                  datePickerType="single"
                  dateFormat="m/d/Y"
                  onChange={eventOrDates => {
                    const value = eventOrDates.target ? eventOrDates.target.value : eventOrDates[0];
                    setCloneDate(value);
                  }}
                >
                  <DatePickerInput
                    invalid={!dateIsValid()}
                    disabled={cloneKeyword !== keywordValueOther}
                    labelText="Clone date"
                    id="date-picker-input"
                    onChange={eventOrDates => {
                      const value = eventOrDates.target
                        ? eventOrDates.target.value
                        : eventOrDates[0];
                      setCloneDate(value);
                    }}
                  />
                </DatePicker>
                <br />
                <TimePicker
                  disabled={cloneKeyword !== keywordValueOther}
                  invalid={!timeIsValid()}
                  value={cloneTime}
                  onChange={eventOrValue => {
                    const value = eventOrValue.target ? eventOrValue.target.value : eventOrValue[0];
                    setCloneTime(value);
                  }}
                >
                  <TimePickerSelect
                    id="time-picker-select-1"
                    labelText="Clone time"
                    onChange={eventOrValue => {
                      const value = eventOrValue.target
                        ? eventOrValue.target.value
                        : eventOrValue[0];
                      setCloneMeridiem(value);
                    }}
                  >
                    <SelectItem value="AM" text="AM" />
                    <SelectItem value="PM" text="PM" />
                  </TimePickerSelect>
                </TimePicker>
              </>
            )}
          </>
        )}
      </ModalWrapper>
    </div>
  );
};

export default CloneModalButton;
